import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { setSelectedOpenClaimsRow } from "../../../stateManagement/actions/claims";
import { Close} from '@mui/icons-material';
import { Checkbox } from "@mui/material";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import './OpenClaimsDetailsModal.css';
import { truncateText } from "../../../features/stocks/RealTimeStock/RealTimeStockUtils";
import { formatDecimal } from "../../../components/dms_common/DmsUtils";
import { Batch } from "../utils/CreateClaimUtils";
import CustomCsPcInput from "../../../components/dms_common/CustomCsPcInput";
import ApprovalModal from "./ApprovalModal";
import ClaimRejectionModal from "./ClaimRejectionModal"
import {getLastNPathSegments, downloadFile} from "../utils/utils";
import RenderSvg from "../../../components/renderSvg/RenderSvg";
import OrderStatusCell from "../../../components/dms_common/OrderStatusCell";
import DownloadModal from '../../../components/claims/viewClaimStatus/DownloadModal';
import { CustomCostField, CustomNumberField } from "../../../components/claims/createNewClaim/DisplayVisibilityCustomFields";
import { popupType } from "../../../types/types";
import { GenericPopUp } from "../../../components/popup/genericPopUp";
import DummyProductPngUrl  from "../../../assets/image/png/dummyProduct.png";
import AssetsSvg from "../../../assets/image/svg/assets.svg";
import OutletSvg from "../../../assets/image/svg/outlet.svg";
import { formatDateToDateMonthYear } from "../../../utils/calenderUtils";
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import "@cyntler/react-doc-viewer/dist/index.css";
import { DownloadOutlined } from '@ant-design/icons';

interface OpenClaimsDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  accordionData: any;
  updateSelectedRowInRows: (updatedRow: any) => void;
  statusConfig?: any;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleSetCheckedIdArray: (ids: any[]) => void;
  openClaimsOthersUIConfig?: any;
  canApproveReject?:boolean;
  approvalConfig?: any;
}

const OpenClaimsDetailsModal: React.FC<OpenClaimsDetailsModalProps> = ({ 
  isOpen, 
  onClose, 
  accordionData,
  updateSelectedRowInRows,
  statusConfig,
  setIsModalOpen,
  handleSetCheckedIdArray,
  openClaimsOthersUIConfig = {},
  canApproveReject = true,
  approvalConfig
}) => {
  const {openClaimsRows, selectedOpenClaimsRow, saleReturnsCheckedClaimIds} = useSelector((state: any) => state.claims);
  const dispatch = useDispatch();
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const { caseUnitLabel, otherUnitLabel, packUnitLabel, claimTypes, claimSubTypes, currency, batchIdConfiguration, enableFocPurchaseReturnClaims } = ConfigDictionary;
  const [expandedBatches, setExpandedBatches] = useState<Record<string, boolean>>({});
  const [selectedBatches, setSelectedBatches] = useState<Record<string, boolean>>({});
  const [approvedAmount, setApprovedAmount] = useState<number>(0);
  const [approvedAssetQty, setApprovedAssetQty] = useState<number>(0);
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [approvalModalState, setApprovalModalState] = useState(1);
  const [rejectionModalOpen, setRejectionModalOpen] = useState(false);
  const [unprocessedBatches, setUnprocessedBatches] = useState<Batch[]>([]);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [genericModalState, setGenericModalState] = useState(false);
  const [genericModalMessage, setGenericModalMessage] = useState("");
  const [genericModalType, setGenericModalType] = useState<popupType>("Error");
  const path = getLastNPathSegments(window.location.href, 2);
  const secondLastSegment=path[1];

  useEffect(() => {
    if (selectedOpenClaimsRow && Object.keys(selectedOpenClaimsRow).length > 0 && accordionData.claimType === claimTypes[2]) {
      setApprovedAmount(parseFloat(selectedOpenClaimsRow.approvedClaimAmount.replace(/[^\d.]/g, '')) || 0);
      if(accordionData.typeOfExpense === claimSubTypes[5])
      setApprovedAssetQty(selectedOpenClaimsRow.assetApprovedQty || 0);
    }
    setSelectedBatches({});
    setExpandedBatches({});
  }, [selectedOpenClaimsRow]);

  useEffect(() => {
    return () => {
      setSelectedBatches({});
      setExpandedBatches({});
    };
  }, []);

  const allBatchesProcessed = useMemo(() => {
    if (accordionData.claimType === claimTypes[0]) {
      return selectedOpenClaimsRow?.batchIds?.every(
        (batch: any) => batch?.status
      ) ?? false;
    } else {
      return selectedOpenClaimsRow?.invoices?.every(
        (invoice: any) => invoice?.status
      ) ?? false;
    }
  }, [selectedOpenClaimsRow, accordionData.claimType]);

  useEffect(() => {
    if (allBatchesProcessed && isOpen) {
      const childItems = accordionData.claimType === claimTypes[0] ? selectedOpenClaimsRow.batchIds : selectedOpenClaimsRow.invoices;
      const allStatuses = childItems.map((item: any) => item.status);
      let skuStatus = 'PARTIALLY_APPROVED';

      if (allStatuses.every((status: string) => status === 'APPROVED')) {
        skuStatus = 'APPROVED';
      } else if (allStatuses.every((status: string) => status === 'REJECTED')) {
        skuStatus = 'REJECTED';
      }

      if (selectedOpenClaimsRow.status !== skuStatus) {
        const updatedCurrentRow = {
          ...selectedOpenClaimsRow,
          status: skuStatus,
        };
        updateSelectedRowInRows(updatedCurrentRow);
      }
    }
  }, [allBatchesProcessed, isOpen, selectedOpenClaimsRow, updateSelectedRowInRows, accordionData.claimType]);

  const openPopUp = (message: string, modalType: popupType) => {
    setGenericModalMessage(message);
    setGenericModalState(true);
    setGenericModalType(modalType);
  };

  const isBatchDisabled = (batch: Batch) => {
    const hasRejectedApproval = batch.approvals?.some(
      (approval: any) => approval.status === 'REJECTED'
    );
    return hasRejectedApproval || !canApproveReject;
  };

  const isInvoiceDisabled = (invoice: any) => {
    const hasRejectedApproval = invoice.approvals?.some(
      (approval: any) => approval.status === 'REJECTED'
    );
    return hasRejectedApproval || !canApproveReject;
  };

  const isAnyBatchSelected = useMemo(() => {
    if (accordionData.claimType === claimTypes[0]) {
      if (!selectedOpenClaimsRow?.batchIds) return false;
      return selectedOpenClaimsRow.batchIds.some(
        (batch: Batch | undefined) => batch && batch.batchId && selectedBatches[batch.batchId] && !isBatchDisabled(batch)
      );
    } else if (accordionData.claimType === claimTypes[1]){
      if (!selectedOpenClaimsRow?.invoices) return false;
      return selectedOpenClaimsRow.invoices.some(
        (invoice: any) => invoice && invoice.invoiceNumber && selectedBatches[
          batchIdConfiguration
            ? `${invoice.invoiceNumber}-${invoice.batchIds}`
            : invoice.invoiceNumber
        ] && !isInvoiceDisabled(invoice)
      );
    }
    else
    {return true;}
  }, [selectedOpenClaimsRow, selectedBatches, accordionData.claimType]);
  
  if (!isOpen || !selectedOpenClaimsRow) return null;

  const handleClose = () => {
      onClose();
  };  

  const toggleBatchExpand = (batchId: string) => {
    setExpandedBatches(prev => ({
      ...prev,
      [batchId]: !prev[batchId]
    }));
  };

  const handleBatchSelect = (batchId: string) => {
    setSelectedBatches(prev => ({
      ...prev,
      [batchId]: !prev[batchId]
    }));
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    
    const newSelectedBatches: Record<string, boolean> = {};
    
    if (accordionData.claimType === claimTypes[0] && selectedOpenClaimsRow.batchIds) {
      selectedOpenClaimsRow.batchIds.forEach((batch: Batch) => {
        if (!isBatchDisabled(batch)) {
          newSelectedBatches[batch.batchId] = isChecked;
        }
      });
    } else if (selectedOpenClaimsRow.invoices) {
      selectedOpenClaimsRow.invoices.forEach((invoice: any) => {
        if (!isInvoiceDisabled(invoice)) {
        newSelectedBatches[
          batchIdConfiguration
            ? `${invoice.invoiceNumber}-${invoice.batchIds}`
            : invoice.invoiceNumber
        ] = isChecked;
        }
      });
    }
    setSelectedBatches(newSelectedBatches);
  };

  const handleApproveClick = async () => {
    if (accordionData.claimType === claimTypes[2]) {
      const claimAmount = parseFloat(selectedOpenClaimsRow.claimAmount.replace(/[^\d.]/g, '')) || 0;
      let approvedClaimAmount = claimAmount;
      
      if (openClaimsOthersUIConfig.isApprovedClaimAmountEditable && approvedAmount!==0) {
        approvedClaimAmount = approvedAmount;
      }

      let approvedAssetQtyValue = selectedOpenClaimsRow.assetQty || 0;
      if (openClaimsOthersUIConfig.isAssetQtyEditable && approvedAssetQty!==0) {
        approvedAssetQtyValue = approvedAssetQty;
      }
  
      const updatedRow = {
        ...selectedOpenClaimsRow,
        approvedClaimAmount: `${currency} ${formatDecimal(approvedClaimAmount)}`,
        status: approvedClaimAmount === 0 ? "REJECTED" : "APPROVED",
        ...(accordionData.typeOfExpense === claimSubTypes[5] && {
          assetApprovedQty: approvedAssetQtyValue
        })
      };
  
      updateSelectedRowInRows(updatedRow);
      dispatch(setSelectedOpenClaimsRow(updatedRow));
      handleSetCheckedIdArray(Array.from(new Set([...saleReturnsCheckedClaimIds, selectedOpenClaimsRow.id])));
      
      setApprovalModalState(2);
      setApprovalModalOpen(true);
      return;
    }
    if (!isAnyBatchSelected) return;
    if (accordionData.claimType === claimTypes[0]) {
      let approvedCase = 0;
      let approvedPcs = 0;
      let approvedOthers = 0;
      let approvedClaimAmount = 0;
  
      const updatedBatchIds = selectedOpenClaimsRow.batchIds?.map((batch: any) => {
        const hasRejectedApproval = batch.approvals?.some(
          (approval: any) => approval.status === 'REJECTED'
        );
  
        if (hasRejectedApproval) {
          const rejectionReason = batch.approvals?.find(
            (approval: any) => approval.status === 'REJECTED'
          )?.reason || "Rejected by previous approver";
          
          return {
            ...batch,
            approvedCs: 0,
            approvedPc: 0,
            approvedOther: 0,
            status: "REJECTED",
            rejectionReason,
            returnReasonArray: batch.returnReasonArray?.map((reason: any) => ({
              ...reason,
              approvedCs: 0,
              approvedPc: 0,
              approvedOther: 0
            }))
          };
        }
  
        let currentApprovedCs = batch.approvedCs || 0;
        let currentApprovedPc = batch.approvedPc || 0;
        let currentApprovedOther = batch.approvedOther || 0;
        let currentStatus = batch.status;
  
        if (selectedBatches[batch.batchId]) {
          currentApprovedCs = batch.claimableCs;
          currentApprovedPc = batch.claimablePc;
          currentApprovedOther = batch.claimableOther;
          currentStatus = "APPROVED";
          
          const updatedReturnReasonArray = batch.returnReasonArray?.map((reason: any) => ({
            ...reason,
            approvedCs: reason.cs,
            approvedPc: reason.pc,
            approvedOther: reason.other
          }));
  
          if (currentStatus === "APPROVED") {
            approvedCase += currentApprovedCs;
            approvedPcs += currentApprovedPc;
            approvedOthers += currentApprovedOther;
            approvedClaimAmount += batch.claimAmount || 0;
          }
  
          return {
            ...batch,
            approvedCs: currentApprovedCs,
            approvedPc: currentApprovedPc,
            approvedOther: currentApprovedOther,
            returnReasonArray: updatedReturnReasonArray,
            status: currentStatus
          };
        } else if (!batch.status) {
          currentStatus = "PENDING_REJECTION";
          return {
            ...batch,
            status: currentStatus
          };
        }
  
        if (batch.status === "APPROVED") {
          approvedCase += batch.approvedCs || 0;
          approvedPcs += batch.approvedPc || 0;
          approvedOthers += batch.approvedOther || 0;
          approvedClaimAmount += batch.claimAmount || 0;
        }
  
        return batch;
      });
  
      const unprocessedBatches = updatedBatchIds?.filter((batch: any) => 
        batch.status === "PENDING_REJECTION" && 
        !batch.approvals?.some((approval: any) => approval.status === 'REJECTED')
      ) || [];
      
      const updatedRow = {
        ...selectedOpenClaimsRow,
        batchIds: updatedBatchIds,
        approvedCase,
        approvedPcs,
        approvedOthers,
        approvedClaimAmount: `${currency} ${formatDecimal(approvedClaimAmount)}`
      };
  
      updateSelectedRowInRows(updatedRow);
      dispatch(setSelectedOpenClaimsRow(updatedRow));
      handleSetCheckedIdArray(Array.from(new Set([...saleReturnsCheckedClaimIds, selectedOpenClaimsRow.id])));
      
      if (unprocessedBatches.length > 0) {
        setUnprocessedBatches(unprocessedBatches);
        setRejectionModalOpen(true);
      } else {
        setApprovalModalState(2);
        setApprovalModalOpen(true);
      }
      return;
    }
    else if (accordionData.claimType === claimTypes[1]) {
      let approvedCase = 0;
      let approvedPcs = 0;
      let approvedOthers = 0;
      let approvedClaimAmount = 0;
  
      const updatedInvoices = selectedOpenClaimsRow.invoices?.map((invoice: any) => {
        const hasRejectedApproval = invoice.approvals?.some(
          (approval: any) => approval.status === 'REJECTED'
        );
  
        if (hasRejectedApproval) {
          const rejectionReason = invoice.approvals?.find(
            (approval: any) => approval.status === 'REJECTED'
          )?.reason || "Rejected by previous approver";
          
          return {
            ...invoice,
            approvedCs: 0,
            approvedPc: 0,
            approvedOther: 0,
            status: "REJECTED",
            rejectionReason
          };
        }
  
        let currentApprovedCs = invoice.approvedCs || 0;
        let currentApprovedPc = invoice.approvedPc || 0;
        let currentApprovedOther = invoice.approvedOther || 0;
        let currentStatus = invoice.status;
  
        if (selectedBatches[batchIdConfiguration
          ? `${invoice.invoiceNumber}-${invoice.batchIds}`
          : invoice.invoiceNumber]) {
          currentApprovedCs = invoice.cases;
          currentApprovedPc = invoice.pieces;
          currentApprovedOther = invoice.others;
          currentStatus = "APPROVED";
          
          if (currentStatus === "APPROVED") {
            approvedCase += currentApprovedCs;
            approvedPcs += currentApprovedPc;
            approvedOthers += currentApprovedOther;
            approvedClaimAmount += invoice.discount || 0;
          }
  
          return {
            ...invoice,
            approvedCs: currentApprovedCs,
            approvedPc: currentApprovedPc,
            approvedOther: currentApprovedOther,
            status: currentStatus
          };
        } else if (!invoice.status) {
          currentStatus = "PENDING_REJECTION";
          return {
            ...invoice,
            status: currentStatus
          };
        }
  
        if (invoice.status === "APPROVED") {
          approvedCase += invoice.approvedCs || 0;
          approvedPcs += invoice.approvedPc || 0;
          approvedOthers += invoice.approvedOther || 0;
          approvedClaimAmount += invoice.discount || 0;
        }
  
        return invoice;
      });
  
      const unprocessedInvoices = updatedInvoices?.filter((invoice: any) => 
        invoice.status === "PENDING_REJECTION" && 
        !invoice.approvals?.some((approval: any) => approval.status === 'REJECTED')
      ) || [];
      
      const updatedRow = {
        ...selectedOpenClaimsRow,
        invoices: updatedInvoices,
        approvedCase,
        approvedPcs,
        approvedOthers,
        approvedClaimAmount: `${currency} ${formatDecimal(approvedClaimAmount)}`
      };
  
      updateSelectedRowInRows(updatedRow);
      dispatch(setSelectedOpenClaimsRow(updatedRow));
      handleSetCheckedIdArray(Array.from(new Set([...saleReturnsCheckedClaimIds, selectedOpenClaimsRow.id])));
      
      if (unprocessedInvoices.length > 0) {
        setUnprocessedBatches(unprocessedInvoices);
        setRejectionModalOpen(true);
      } else {
        setApprovalModalState(2);
        setApprovalModalOpen(true);
      }
      return;
    }
  };
  
  const handleRejectionClick = async (reasons: Record<string, string>) => {
    if (accordionData.claimType === claimTypes[2]) {
      const rejectionReason = reasons[selectedOpenClaimsRow.outletCode] || "Rejected";
      
      const updatedRow = {
        ...selectedOpenClaimsRow,
        approvedClaimAmount: `${currency} 0.00`,
        status: "REJECTED",
        rejectionReason: rejectionReason,
        ...(accordionData.typeOfExpense === claimSubTypes[5] && {
          assetApprovedQty: 0
        })
      };      
  
      updateSelectedRowInRows(updatedRow);
      dispatch(setSelectedOpenClaimsRow(updatedRow));
      handleSetCheckedIdArray(Array.from(new Set([...saleReturnsCheckedClaimIds, selectedOpenClaimsRow.id])));
      setApprovalModalState(2);
      setApprovalModalOpen(true);
      return;
    }
    if (!isAnyBatchSelected && !unprocessedBatches.length) return;

    if (accordionData.claimType === claimTypes[0]) {
      let approvedCase = 0;
      let approvedPcs = 0;
      let approvedOthers = 0;
      let approvedClaimAmount = 0;
  
      const updatedBatchIds = selectedOpenClaimsRow.batchIds?.map((batch: any) => {
        const hasRejectedApproval = batch.approvals?.some(
          (approval: any) => approval.status === 'REJECTED'
        );
  
        if (hasRejectedApproval) {
          const rejectionReason = batch.approvals?.find(
            (approval: any) => approval.status === 'REJECTED'
          )?.reason || "Rejected by previous approver";
          
          return {
            ...batch,
            approvedCs: 0,
            approvedPc: 0,
            approvedOther: 0,
            status: "REJECTED",
            rejectionReason,
            returnReasonArray: batch.returnReasonArray?.map((reason: any) => ({
              ...reason,
              approvedCs: 0,
              approvedPc: 0,
              approvedOther: 0
            }))
          };
        }
  
        if (selectedBatches[batch.batchId] || batch.status === "PENDING_REJECTION") {
          return {
            ...batch,
            approvedCs: 0,
            approvedPc: 0,
            approvedOther: 0,
            status: "REJECTED",
            rejectionReason: reasons[batch.batchId] || "Rejected",
            returnReasonArray: batch.returnReasonArray?.map((reason: any) => ({
              ...reason,
              approvedCs: 0,
              approvedPc: 0,
              approvedOther: 0
            }))
          };
        } else if (!batch.status) {
          const updatedReturnReasonArray = batch.returnReasonArray?.map((reason: any) => ({
            ...reason,
            approvedCs: reason.cs,
            approvedPc: reason.pc,
            approvedOther: reason.other
          }));
  
          approvedCase += batch.claimableCs || 0;
          approvedPcs += batch.claimablePc || 0;
          approvedOthers += batch.claimableOther || 0;
          approvedClaimAmount += batch.claimAmount || 0;
  
          return {
            ...batch,
            approvedCs: batch.claimableCs,
            approvedPc: batch.claimablePc,
            approvedOther: batch.claimableOther,
            returnReasonArray: updatedReturnReasonArray,
            status: "APPROVED"
          };
        }
        if (batch.status === "APPROVED") {
          approvedCase += batch.approvedCs || 0;
          approvedPcs += batch.approvedPc || 0;
          approvedOthers += batch.approvedOther || 0;
          approvedClaimAmount += batch.claimAmount || 0;
        }
        return batch;
      });
  
      const allStatuses = updatedBatchIds?.map((batch: any) => batch.status) || [];
      let skuStatus = "PARTIALLY_APPROVED";
      if (allStatuses.every((status: string) => status === "APPROVED")) {
        skuStatus = "APPROVED";
      } else if (allStatuses.every((status: string) => status === "REJECTED")) {
        skuStatus = "REJECTED";
      }
  
      const updatedRow = {
        ...selectedOpenClaimsRow,
        batchIds: updatedBatchIds,
        approvedCase,
        approvedPcs,
        approvedOthers,
        approvedClaimAmount: `${currency} ${formatDecimal(approvedClaimAmount)}`,
        status: skuStatus
      };
  
      updateSelectedRowInRows(updatedRow);
      dispatch(setSelectedOpenClaimsRow(updatedRow));
      handleSetCheckedIdArray(Array.from(new Set([...saleReturnsCheckedClaimIds, selectedOpenClaimsRow.id])));
      setApprovalModalState(2);
      setApprovalModalOpen(true);
      return;
    }
    else if (accordionData.claimType === claimTypes[1]) {
      let approvedCase = 0;
      let approvedPcs = 0;
      let approvedOthers = 0;
      let approvedClaimAmount = 0;
  
      const updatedInvoices = selectedOpenClaimsRow.invoices?.map((invoice: any) => {
        const hasRejectedApproval = invoice.approvals?.some(
          (approval: any) => approval.status === 'REJECTED'
        );
  
        if (hasRejectedApproval) {
          const rejectionReason = invoice.approvals?.find(
            (approval: any) => approval.status === 'REJECTED'
          )?.reason || "Rejected by previous approver";
          
          return {
            ...invoice,
            approvedCs: 0,
            approvedPc: 0,
            approvedOther: 0,
            status: "REJECTED",
            rejectionReason
          };
        }
  
        if (selectedBatches[batchIdConfiguration
          ? `${invoice.invoiceNumber}-${invoice.batchIds}`
          : invoice.invoiceNumber] || invoice.status === "PENDING_REJECTION") {
          return {
            ...invoice,
            approvedCs: 0,
            approvedPc: 0,
            approvedOther: 0,
            status: "REJECTED",
            rejectionReason: reasons[batchIdConfiguration
              ? `${invoice.invoiceNumber}-${invoice.batchIds}`
              : invoice.invoiceNumber] || "Rejected",
          };
        } else if (!invoice.status) {
          const updatedInvoice = {
            ...invoice,
            approvedCs: invoice.cases,
            approvedPc: invoice.pieces,
            approvedOther: invoice.others,
            status: "APPROVED",
          };
          approvedCase += updatedInvoice.approvedCs;
          approvedPcs += updatedInvoice.approvedPc;
          approvedOthers += updatedInvoice.approvedOther;
          approvedClaimAmount += invoice.discount || 0;
          return updatedInvoice;
        }
        
        if (invoice.status === "APPROVED") {
          approvedCase += invoice.approvedCs || 0;
          approvedPcs += invoice.approvedPc || 0;
          approvedOthers += invoice.approvedOther || 0;
          approvedClaimAmount += invoice.discount || 0;
        }
        return invoice;
      });
  
      const allStatuses = updatedInvoices?.map((invoice: any) => invoice.status) || [];
      let skuStatus = "PARTIALLY_APPROVED";
      if (allStatuses.every((status: string) => status === "APPROVED")) {
        skuStatus = "APPROVED";
      } else if (allStatuses.every((status: string) => status === "REJECTED")) {
        skuStatus = "REJECTED";
      }
  
      const updatedRow = {
        ...selectedOpenClaimsRow,
        invoices: updatedInvoices,
        approvedCase,
        approvedPcs,
        approvedOthers,
        approvedClaimAmount: `${currency} ${formatDecimal(approvedClaimAmount)}`,
        status: skuStatus
      };
  
      updateSelectedRowInRows(updatedRow);
      dispatch(setSelectedOpenClaimsRow(updatedRow));
      handleSetCheckedIdArray(Array.from(new Set([...saleReturnsCheckedClaimIds, selectedOpenClaimsRow.id])));
      setApprovalModalState(2);
      setApprovalModalOpen(true);
      return;
    }
  };

const getSelectedBatchesArray = () => {
  if (accordionData.claimType === claimTypes[0]) {
    if (!selectedOpenClaimsRow?.batchIds) return [];
    return selectedOpenClaimsRow.batchIds
      .filter((batch: any) => batch.status === "PENDING_REJECTION" || selectedBatches[batch.batchId])
      .map((batch: any) => ({
        id: batch.batchId,
        checked: !!selectedBatches[batch.batchId] || batch.status === "PENDING_REJECTION",
        type: "batch",
        skuCode: selectedOpenClaimsRow.skuCode,
        batchId: batch.batchId,
        claimablePc: batch.claimablePc,
        claimableCs: batch.claimableCs,
        claimableOther: batch.claimableOther
      }));
  } else if (accordionData.claimType === claimTypes[1]) {
    if (!selectedOpenClaimsRow?.invoices) return [];
    return selectedOpenClaimsRow.invoices
      .filter((invoice: any) => invoice.status === "PENDING_REJECTION" || selectedBatches[
        batchIdConfiguration
          ? `${invoice.invoiceNumber}-${invoice.batchIds}`
          : invoice.invoiceNumber
      ])
      .map((invoice: any) => ({
        id: batchIdConfiguration
            ? `${invoice.invoiceNumber}-${invoice.batchIds}`
            : invoice.invoiceNumber,
          checked:
            !!selectedBatches[
              batchIdConfiguration
                ? `${invoice.invoiceNumber}-${invoice.batchIds}`
                : invoice.invoiceNumber
            ] || invoice.status === "PENDING_REJECTION",
        type: "invoice",
        skuCode: selectedOpenClaimsRow.skuCode,
        remark: selectedOpenClaimsRow.remark,
        invoiceNumber: invoice.invoiceNumber,
        outletName: invoice.outletName,
        outletCode: invoice.outletCode,
      }));
  } else {
    return [{
      id: selectedOpenClaimsRow.outletCode,
      checked: true,
      type: "others",
      outletName: selectedOpenClaimsRow.outletName,
      outletCode: selectedOpenClaimsRow.outletCode
    }];
  }
};

  const getSelectedStatus = () => {
    const updatedStatusConfig = statusConfig.map((config: any) => ({
      ...config,
      id: config.id?.toUpperCase(),
    }));

    if (enableFocPurchaseReturnClaims && selectedOpenClaimsRow.stockType === "foc") {
      const approvedCase = selectedOpenClaimsRow.approvedCase || 0;
      const approvedPcs = selectedOpenClaimsRow.approvedPcs || 0;
      const approvedOthers = selectedOpenClaimsRow.approvedOthers || 0;
      
      const damageCase = selectedOpenClaimsRow.damageCase || 0;
      const damagePcs = selectedOpenClaimsRow.damagePcs || 0;
      const damageOthers = selectedOpenClaimsRow.damageOthers || 0;
      
      if (approvedCase === 0 && approvedPcs === 0 && approvedOthers === 0) {
        return updatedStatusConfig[2].id; // REJECTED
      } else if (approvedCase === damageCase && approvedPcs === damagePcs && approvedOthers === damageOthers) {
        return updatedStatusConfig[0].id; // APPROVED
      } else {
        return updatedStatusConfig[3].id; // PARTIALLY_APPROVED
      }
    }

    const approvedAmount = parseFloat(selectedOpenClaimsRow.approvedClaimAmount?.replace(/[^\d.]/g, '') || '0');
    const claimAmount = parseFloat(selectedOpenClaimsRow.claimAmount?.replace(/[^\d.]/g, '') || '0');
  
    if (approvedAmount === 0) {
      return updatedStatusConfig[2].id;
    } else if (approvedAmount === claimAmount) {
      return updatedStatusConfig[0].id;
    } else if (approvedAmount > 0 && accordionData.claimType === claimTypes[2]){
      return updatedStatusConfig[0].id;
    }
    else {
      return updatedStatusConfig[3].id;
    }
  };  

    const damageQty = [
    caseUnitLabel && (
      <span key="cases">
        <span className="open-claims-qty-number">{selectedOpenClaimsRow.damageCase}</span>
        <span className="open-claims-qty-unit"> {caseUnitLabel}</span>
      </span>
    ),
    (selectedOpenClaimsRow?.otherUnitName || otherUnitLabel) && (
      <span key="others">
      <span className="open-claims-qty-number">{selectedOpenClaimsRow.damageOthers}</span>
      <span className="open-claims-qty-unit"> {selectedOpenClaimsRow?.otherUnitName || otherUnitLabel}</span>
    </span>
    ),
    packUnitLabel && (
      <span key="pieces">
        <span className="open-claims-qty-number">{selectedOpenClaimsRow.damagePcs}</span>
        <span className="open-claims-qty-unit"> {packUnitLabel}</span>
      </span>
    )
  ].filter(Boolean).reduce((acc: React.ReactNode[], element, index, array) => {
    if (index > 0) {
      acc.push(<span key={`comma-${index}`}>, </span>);
    }
    acc.push(element);
    return acc;
  }, []);

  const handleDownloadAll = async () => {
    const files = selectedOpenClaimsRow.blobUrl;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileName = file.split('/').pop() ?? 'file';
      await downloadFile(file, fileName);
    }
  };

  const handleOpenDownloadModal = (files: any) => {
    setSelectedFiles(files);
    setIsDownloadModalOpen(true);
  };
  
  const handleCloseDownloadModal = () => {
    setIsDownloadModalOpen(false);
    setSelectedFiles([]);
  };

  const handleApprovedAmountChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = parseFloat(e.target.value) || 0;
    const claimAmount = parseFloat(selectedOpenClaimsRow.claimAmount.replace(/[^\d.]/g, '')) || 0;
    
    if (newValue < 0) {
      openPopUp("Negative numbers are not allowed", "Alert");
      return;
    }
    
    if (newValue > claimAmount) {
      openPopUp("Approved claim amount can't exceed claimed amount", "Alert");
      return;
    }
    setApprovedAmount(newValue);
  };

  const handleApprovedAssetQtyChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = parseInt(e.target.value) || 0;
    const assetQty = selectedOpenClaimsRow.assetQty || 0;
    
    if (newValue < 0) {
      openPopUp("Negative numbers are not allowed", "Alert");
      return;
    }
    
    if (newValue > assetQty) {
      openPopUp("Approved asset quantity can't exceed claimed quantity", "Alert");
      return;
    }
    setApprovedAssetQty(newValue);
  };

  const getImageSrc = () => {
    if (accordionData.typeOfExpense === claimSubTypes[4]) {
      return OutletSvg;
    } else if (accordionData.typeOfExpense === claimSubTypes[5]) {
      return AssetsSvg;
    } else {
      return selectedOpenClaimsRow?.imgBlobUrl || DummyProductPngUrl;
    }
  };
  
  return (
    <>
      <GenericPopUp
        message={genericModalMessage}
        type={genericModalType}
        openGenericModal={genericModalState}
        setOpenGenericModal={() => setGenericModalState(false)}
      />
      {isDownloadModalOpen && (
        <DownloadModal
          isOpen={isDownloadModalOpen}
          onClose={handleCloseDownloadModal}
          files={selectedFiles}
          outletName={selectedOpenClaimsRow.outletName || "N/A"}
        />
      )}
    <div className="open-claims-sku-detail-modal">
      <div className="open-claims-header">
        {accordionData.typeOfExpense === claimSubTypes[2] && (
          <>
            <RenderSvg name="discountIcon" style={{ "height": "30px", "margin-right": "5px" }} />
            <p className="open-claims-remark claims-text-ellipsis" title={selectedOpenClaimsRow.remark}>{truncateText(selectedOpenClaimsRow.remark,40)}</p>          </>)}
        <button className="open-claims-close-button" onClick={handleClose}>
          <Close />
        </button>
      </div>

        <div className="open-claims-modal-content">
          <div className="open-claims-sku-info">
            <div className="open-claims-sku-image-container">
              <img src={getImageSrc()} alt={accordionData.claimType === claimTypes[2] ? "Outlet Image" : "SKU Image"} className="open-claims-sku-image" style={accordionData.typeOfExpense === claimSubTypes[4] ? { width: "80%", height: "80%" } : undefined} />
            </div>
            <div className="open-claims-sku-details">
              <h4
                className="open-claims-title claims-text-ellipsis"
                title={accordionData.typeOfExpense === claimSubTypes[4] ? selectedOpenClaimsRow?.outletName : accordionData.typeOfExpense === claimSubTypes[5] ? selectedOpenClaimsRow?.assetType : selectedOpenClaimsRow?.skuName}
              >
                {accordionData.typeOfExpense === claimSubTypes[4]
                  ? selectedOpenClaimsRow?.outletName
                  : accordionData.typeOfExpense === claimSubTypes[5]
                    ? selectedOpenClaimsRow?.assetType
                    : selectedOpenClaimsRow?.skuName}
              </h4>
              <p className="open-claims-sku-code claims-text-ellipsis">{accordionData.typeOfExpense === claimSubTypes[4] ? `Outlet Code: ${selectedOpenClaimsRow?.outletCode}` : accordionData.typeOfExpense === claimSubTypes[5] ? `Asset ID: ${selectedOpenClaimsRow?.assetId}` : `SKU Code: ${selectedOpenClaimsRow?.skuCode}`}{enableFocPurchaseReturnClaims && selectedOpenClaimsRow?.stockType === "foc" && (
                <RenderSvg name="focIcon" />
              )}</p>
              {selectedOpenClaimsRow.returnOrderNo !== "-" ? <p className="open-claims-sku-code claims-text-ellipsis" title={selectedOpenClaimsRow.returnOrderNo}>{selectedOpenClaimsRow.returnOrderNo}</p> : <p></p>}
            </div>
          </div>
          {accordionData.claimType === claimTypes[2] ?
            (accordionData.typeOfExpense === claimSubTypes[4] ?
              <div className="open-claims-dates-container">
                <div className="open-claims-date-item">
                  <span className="open-claims-damage-qty-label">Display Start Date</span>
                  <span className="open-claims-display-start-date">
                    {formatDateToDateMonthYear(selectedOpenClaimsRow.displayStartDate)}
                  </span>
                </div>
                <div className="open-claims-date-item">
                  <span className="open-claims-damage-qty-label">Display End Date</span>
                  <span className="open-claims-display-end-date">
                    {formatDateToDateMonthYear(selectedOpenClaimsRow.displayEndDate)}
                  </span>
                </div>
              </div> : <></>) :
            <div className="open-claims-damage-qty-container">
              <div className="open-claims-damage-qty-content">
                <div className="open-claims-amount-block">
                  <span className="open-claims-damage-qty-label">Total Quantity</span>
                  <span className="open-claims-damage-qty-value">
                    {damageQty.length > 0 ? damageQty : <span className="open-claims-qty-number">0</span>}
                  </span>
                </div>
                {accordionData.claimType === claimTypes[1] ?
                  <div className="open-claims-amount-block">
                    <span className="open-claims-discount-amount-label">Discount Amount</span>
                    <span className="open-claims-discount-amount-value">
                      {selectedOpenClaimsRow.claimAmount}
                    </span>
                  </div> : <></>
                }
              </div>
            </div>
          }
          {accordionData.claimType === claimTypes[2] ?
            <>
              <div className="open-claims-attachments-container">
                <div className="open-claims-attachments-header">
                  Attachments
                </div>
                <div className="open-claims-attachments-preview">
                  <DocViewer
                    key={selectedOpenClaimsRow.id}
                    documents={[{ uri: selectedOpenClaimsRow.blobUrl[0], fileType: selectedOpenClaimsRow.blobUrl[0].split('.').pop() ?? 'unknown' }]}
                    initialActiveDocument={{ uri: selectedOpenClaimsRow.blobUrl[0] }}
                    pluginRenderers={DocViewerRenderers}
                    config={{
                      header: {
                        disableHeader: true,
                        disableFileName: false,
                        retainURLParams: false,
                      },
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      backgroundColor: 'white',
                    }}
                  />
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '8px',
                    alignItems: 'center'
                  }}
                >
                  <button
                    className="circular-icon-wrapper"
                    onClick={() => handleOpenDownloadModal(selectedOpenClaimsRow.blobUrl)}
                  >
                    <RenderSvg name="viewIcon" />
                  </button>
                  <button
                    className="circular-icon-wrapper"
                    onClick={handleDownloadAll}
                  >
                    <DownloadOutlined style={{ color: '#02c6b1', fontSize: '18px' }} />
                  </button>
                </div>
              </div>
              {secondLastSegment === "closed" && parseFloat(selectedOpenClaimsRow.approvedClaimAmount.replace(/[^\d.]/g, '')) === 0 && (
                <div className="open-claims-outlet-container">
                  <div className="open-claims-outlet-info">
                    <div className="open-claims-outlet-label" style={{ color: "#C44C36" }}>Rejection Reason</div>
                    <div className="open-claims-outlet-code">{selectedOpenClaimsRow.claimsRejectionReason}</div>
                  </div>
                </div>
              )}
              {(secondLastSegment === "open" && accordionData.typeOfExpense === claimSubTypes[5] && openClaimsOthersUIConfig.isAssetQtyEditable) ?
                <div className='open-claims-amount-boxes'>
                  <div style={{ flex: 1 }}>
                    <span className="open-claims-damage-qty-label">Asset Quantity</span>
                    <span className="open-claims-amount-label">
                      {selectedOpenClaimsRow.assetQty}
                    </span>
                  </div>
                  <div>
                    <span className="open-claims-damage-qty-label">Approved Asset Quantity</span>
                    <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                      <CustomNumberField
                        isOpenClaims={true}
                        label="Approved Asset Quantity"
                        name="approvedAssetQty"
                        value={approvedAssetQty}
                        placeholder="0"
                        onChange={handleApprovedAssetQtyChange}
                        required={false}
                      />
                    </div>
                  </div>
                </div> : <></>}
              {(secondLastSegment === "open" && openClaimsOthersUIConfig.isApprovedClaimAmountEditable) ?
                <div className='open-claims-amount-boxes'>
                  <div style={{ flex: 1 }}>
                    <span className="open-claims-damage-qty-label">Claim Amount</span>
                    <span className="open-claims-amount-label">
                      {selectedOpenClaimsRow.claimAmount}
                    </span>
                  </div>
                  <div>
                    <span className="open-claims-damage-qty-label">Approved Claim Amount</span>
                    <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                      <CustomCostField
                        isOpenClaims={true}
                        label="Approved Claim Amount"
                        name="approvedClaimAmount"
                        value={approvedAmount}
                        currencySymbol={currency}
                        placeholder="0"
                        onChange={handleApprovedAmountChange}
                        required={false}
                      />
                    </div>
                  </div>
                </div> : <></>}
            </>
            : <div className="open-claims-batch-section" onWheel={(e) => e.stopPropagation()}>
              <div className="open-claims-batch-header">
                {accordionData.claimType === claimTypes[0] ?
                  <span>{selectedOpenClaimsRow.batchIds?.length || 0}{" "}{selectedOpenClaimsRow.batchIds?.length === 1 ? "Batch ID" : "Batch IDs"}</span> :
                  accordionData.claimType === claimTypes[1] ?
                    <span>{selectedOpenClaimsRow.invoices?.length || 0}{" "}{selectedOpenClaimsRow.invoices?.length === 1 ? "Invoice" : "Invoices"}</span> :
                    <></>}
                {secondLastSegment === "open" && accordionData.claimType !== claimTypes[2] ?
                  <div className="open-claims-select-all">
                    <label>
                      Select All
                      <Checkbox
                        sx={{
                          color: "#909090",
                          "&.Mui-checked": { color: "#00bfa5" },
                          "& .MuiSvgIcon-root": { fontSize: "22px" },
                        }}
                        checked={accordionData.claimType === claimTypes[0] ? (selectedOpenClaimsRow.batchIds ?
                          selectedOpenClaimsRow.batchIds.every((batch: Batch) => selectedBatches[batch.batchId]) :
                          false) :
                          (selectedOpenClaimsRow.invoices ?
                            selectedOpenClaimsRow.invoices.every((invoice: any) => selectedBatches[
                              batchIdConfiguration
                                ? `${invoice.invoiceNumber}-${invoice.batchIds}`
                                : invoice.invoiceNumber
                            ]) :
                            false)
                        }
                        onChange={handleSelectAll}
                      />
                    </label>
                  </div> : <></>}
              </div>
              <div className="open-claims-batch-list">
                {accordionData.claimType === claimTypes[0] ? (
                  selectedOpenClaimsRow.batchIds?.map((batch: any, index: number) => {

                    const hasRejectedApproval = batch.approvals?.some((approval: any) => approval.status === 'REJECTED');
                    const hasApprovedApproval = batch.approvals?.some((approval: any) => approval.status === 'APPROVED');
                    const isApproved = batch.status === "APPROVED";
                    const isRejected = batch.status === "REJECTED";
                    let borderColor = isApproved ? "#00C6B1" : isRejected ? "#C44C36" : "#dfe2e7";
                    let backgroundColor = isApproved ? "#FAFFFF" : isRejected ? "#FFFAFA" : "#FFFFFF";
                    if (secondLastSegment === "closed") {
                      if (hasRejectedApproval) {
                        borderColor = "#C44C36";
                        backgroundColor = "#FFFAFA";
                      } else if (hasApprovedApproval) {
                        borderColor = "#00C6B1";
                        backgroundColor = "#FAFFFF";
                      }
                    }

                    return (
                      <div key={index} className="open-claims-batch-item">
                        <div className={`open-claims-batch-item-header ${expandedBatches[batch.batchId] ? 'expanded' : ''}`}>
                          <div className="open-claims-batch-info">
                            {secondLastSegment === "open" ?
                              <Checkbox
                                sx={{
                                  color: "#909090",
                                  "&.Mui-checked": { color: "#00bfa5" },
                                  "& .MuiSvgIcon-root": { fontSize: "20px" },
                                }}
                                checked={!!selectedBatches[batch.batchId]}
                                onChange={() => handleBatchSelect(batch.batchId)}
                                disabled={isBatchDisabled(batch)}
                              /> : <></>}
                            <div className="open-claims-batch-id claims-text-ellipsis" title={batch.batchId} style={secondLastSegment === "closed" ? { marginLeft: "8px" } : {}}>
                              {batch.batchId}</div>
                            <div className="open-claims-open-batch-inputs-container">
                              {caseUnitLabel && (
                                <CustomCsPcInput
                                  value={batch.claimableCs}
                                  onChange={() => null}
                                  label={caseUnitLabel}
                                  percentage={85}
                                  borderColor={borderColor}
                                  backgroundColor={backgroundColor}
                                  readOnly={true}
                                />
                              )}
                              {(selectedOpenClaimsRow?.otherUnitName || otherUnitLabel) && (
                                <CustomCsPcInput
                                  value={batch.claimableOther}
                                  onChange={() => null}
                                  label={selectedOpenClaimsRow?.otherUnitName || otherUnitLabel}
                                  percentage={85}
                                  borderColor={borderColor}
                                  backgroundColor={backgroundColor}
                                  readOnly={true}
                                />
                              )}
                              {packUnitLabel && (
                                <CustomCsPcInput
                                  value={batch.claimablePc}
                                  onChange={() => null}
                                  label={packUnitLabel}
                                  percentage={85}
                                  borderColor={borderColor}
                                  backgroundColor={backgroundColor}
                                  readOnly={true}
                                />
                              )}
                            </div>

                            <div
                              className="open-claims-batch-dropdown"
                              onClick={() => toggleBatchExpand(batch.batchId)}
                            >
                              {expandedBatches[batch.batchId] ? <ExpandLess /> : <ExpandMore />}
                            </div>
                          </div>
                        </div>

                        {expandedBatches[batch.batchId] && (
                          <div className="open-claims-reason-list">
                            {batch.returnReasonArray?.map((reason: any, rIndex: any) => {
                              return (
                                <div key={rIndex} className="open-claims-reason-item">
                                <div className="open-claims-reason-text" title={reason.reason}>{reason.reason}</div>
                                <div className="open-claims-reason-qty">
                                    <div className="open-claims-open-batch-inputs-container">
                                      {caseUnitLabel && (
                                        <CustomCsPcInput
                                          value={reason.cs}
                                          onChange={() => null}
                                          label={caseUnitLabel}
                                          percentage={85}
                                          borderColor={borderColor}
                                          backgroundColor={backgroundColor}
                                          readOnly={true}
                                        />
                                      )}
                                      {(selectedOpenClaimsRow?.otherUnitName || otherUnitLabel) && (
                                        <CustomCsPcInput
                                          value={reason.other}
                                          onChange={() => null}
                                          label={selectedOpenClaimsRow?.otherUnitName || otherUnitLabel}
                                          percentage={85}
                                          borderColor={borderColor}
                                          backgroundColor={backgroundColor}
                                          readOnly={true}
                                        />
                                      )}
                                      {packUnitLabel && (
                                        <CustomCsPcInput
                                          value={reason.pc}
                                          onChange={() => null}
                                          label={packUnitLabel}
                                          percentage={85}
                                          borderColor={borderColor}
                                          backgroundColor={backgroundColor}
                                          readOnly={true}
                                        />
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )
                            })}
                            {batch.approvals?.length > 0 && (
                              <div className="open-claims-approval-section">
                                {batch.approvals.some((approval: any) => approval.status !== 'PENDING') && (
                                  <div className="open-claims-batch-id" style={{ fontWeight: 500, marginBottom: "4px" }}>
                                    Approved / Rejected Qty by                                    </div>
                                )}
                                {batch.approvals
                                  .filter((approval: any) => approval.status !== 'PENDING')
                                  .reduce((acc: any[], approval: any) => {
                                    const existing = acc.find(
                                      (item) =>
                                        item.designation === approval.designation &&
                                        item.userId === approval.userId
                                    );
                                    if (existing) {
                                      const existingQtys = existing.approvedQuantity?.split('.')?.map(Number) || [0, 0, 0];
                                      const newQtys = approval.approvedQuantity?.split('.')?.map(Number) || [0, 0, 0];

                                      existing.approvedQuantity = `${existingQtys[0] + newQtys[0]}.${existingQtys[1] + newQtys[1]}.${existingQtys[2] + newQtys[2]}`;
                                    } else {
                                      acc.push({ ...approval });
                                    }
                                    return acc;
                                  }, []).sort((a: { designation: string }, b: { designation: string }) => {
                                    const aLevel = approvalConfig?.flow.findIndex((level: any) =>
                                      level.includes(a.designation))
                                    const bLevel = approvalConfig?.flow.findIndex((level: any) =>
                                      level.includes(b.designation))
                                    return bLevel - aLevel;
                                  })
                                  .map((approval: any, aIndex: number) => {
                                    const isApproved = approval.status === 'APPROVED';
                                    const isRejected = approval.status === 'REJECTED';
                                    const borderColor = isApproved ? "#00C6B1" : isRejected ? "#C44C36" : "#dfe2e7";
                                    const backgroundColor = isApproved ? "#FAFFFF" : isRejected ? "#FFFAFA" : "#FFFFFF";

                                    return (
                                      <div key={`approval-${aIndex}`} className="open-claims-approval-item">
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                          <div className="open-claims-approval-designation claims-text-ellipsis" title={approval.designation || 'Unknown'}>
                                            {approval.designation || 'Unknown'}
                                          </div>
                                          <div className="open-claims-open-batch-inputs-container">
                                            {caseUnitLabel && (
                                              <CustomCsPcInput
                                                value={batch.claimableCs}
                                                onChange={() => null}
                                                label={caseUnitLabel}
                                                percentage={85}
                                                borderColor={borderColor}
                                                backgroundColor={backgroundColor}
                                                readOnly={true}
                                              />
                                            )}
                                            {(selectedOpenClaimsRow?.otherUnitName || otherUnitLabel) && (
                                              <CustomCsPcInput
                                                value={batch.claimableOther}
                                                onChange={() => null}
                                                label={selectedOpenClaimsRow?.otherUnitName || otherUnitLabel}
                                                percentage={85}
                                                borderColor={borderColor}
                                                backgroundColor={backgroundColor}
                                                readOnly={true}
                                              />
                                            )}
                                            {packUnitLabel && (
                                              <CustomCsPcInput
                                                value={batch.claimablePc}
                                                onChange={() => null}
                                                label={packUnitLabel}
                                                percentage={85}
                                                borderColor={borderColor}
                                                backgroundColor={backgroundColor}
                                                readOnly={true}
                                              />
                                            )}
                                          </div>
                                        </div>
                                        {approval.status === 'REJECTED' && (
                                          <div className="open-claims-rejection-reason">
                                            <div className='open-claims-rejection-label'>
                                              Rejection Reason </div>
                                            {approval.reason || 'No reason provided'}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })) :
                  (
                    selectedOpenClaimsRow.invoices?.map((invoice: any, index: number) => {
                      const hasRejectedApproval = invoice.approvals?.some((approval: any) => approval.status === 'REJECTED');
                      const hasApprovedApproval = invoice.approvals?.some((approval: any) => approval.status === 'APPROVED');
                      const isApproved = invoice.status === "APPROVED";
                      const isRejected = invoice.status === "REJECTED";
                      let borderColor = isApproved ? "#00C6B1" : isRejected ? "#C44C36" : "#dfe2e7";
                      let backgroundColor = isApproved ? "#FAFFFF" : isRejected ? "#FFFAFA" : "#FFFFFF";
                      if (secondLastSegment === "closed") {
                        if (hasRejectedApproval) {
                          borderColor = "#C44C36";
                          backgroundColor = "#FFFAFA";
                        } else if (hasApprovedApproval) {
                          borderColor = "#00C6B1";
                          backgroundColor = "#FAFFFF";
                        }
                      }
                      const uniqueKey = batchIdConfiguration
                      ? `${invoice.invoiceNumber}-${invoice.batchIds}`
                      : invoice.invoiceNumber;
                      return (
                        <div key={index} className="open-claims-batch-item">
                          <div className={`open-claims-batch-item-header ${expandedBatches[uniqueKey] ? 'expanded' : ''}`}>
                            <div className="open-claims-batch-info">
                              {secondLastSegment === "open" && (
                                <Checkbox
                                  sx={{
                                    color: "#909090",
                                    "&.Mui-checked": { color: "#00bfa5" },
                                    "& .MuiSvgIcon-root": { fontSize: "20px" },
                                  }}
                                  checked={!!selectedBatches[uniqueKey]}
                                  onChange={() => handleBatchSelect(uniqueKey)}
                                  disabled={isInvoiceDisabled(invoice)}
                                />
                              )}
                              <div className="open-claims-batch-id claims-text-ellipsis" title={uniqueKey} style={secondLastSegment === "closed" ? { marginLeft: "8px" } : {}}>
                                {batchIdConfiguration
                                  ? `${invoice.invoiceNumber} - ${invoice.batchIds}`
                                  : invoice.invoiceNumber}
                              </div>
                              <div className="open-claims-invoice-amount-container">
                                <div className="open-claims-invoice-amount-box">
                                  <div className="open-claims-invoice-amount-label">Total Amount</div>
                                  <div className="open-claims-invoice-amount-value" style={{ color: "#626262" }}>
                                    {currency} {formatDecimal(invoice.totalAmount)}
                                  </div>
                                </div>
                                <div
                                  className="open-claims-invoice-discount-box"
                                  style={{ borderColor, backgroundColor }}
                                >
                                  <div className="open-claims-invoice-discount-label">Discount Amount</div>
                                  <div className="open-claims-invoice-discount-value">
                                    {currency} {formatDecimal(invoice.discount)}
                                  </div>
                                </div>
                              </div>
                              <div
                                className="open-claims-batch-dropdown"
                                onClick={() => toggleBatchExpand(uniqueKey)}
                              >
                                {expandedBatches[invoice.invoiceNumber] ? <ExpandLess /> : <ExpandMore />}
                              </div>
                            </div>
                          </div>

                          {expandedBatches[uniqueKey] && (
                            <div className="open-claims-reason-list">
                              <div className="open-claims-outlet-container">
                                <div className="open-claims-outlet-info">
                                  <div className="open-claims-outlet-label">Outlet Name & Code</div>
                                  <div className="open-claims-outlet-name claims-text-ellipsis" title={invoice.outletName}>{invoice.outletName}</div>
                                  <div className="open-claims-outlet-code claims-text-ellipsis" title={invoice.outletCode}>({invoice.outletCode})</div>
                                </div>

                                <div className="open-claims-quantity-section">
                                  <div className="open-claims-total-qty-label">Total Qty</div>
                                  <div className="open-claims-outlet-quantities">
                                    {caseUnitLabel && (
                                      <CustomCsPcInput
                                        value={invoice.cases}
                                        onChange={() => null}
                                        label={caseUnitLabel}
                                        percentage={75}
                                        borderColor={borderColor}
                                        backgroundColor={backgroundColor}
                                        readOnly={true}
                                      />
                                    )}
                                    {(selectedOpenClaimsRow?.otherUnitName || otherUnitLabel) && (
                                      <CustomCsPcInput
                                        value={invoice.others}
                                        onChange={() => null}
                                        label={selectedOpenClaimsRow?.otherUnitName || otherUnitLabel}
                                        percentage={75}
                                        borderColor={borderColor}
                                        backgroundColor={backgroundColor}
                                        readOnly={true}
                                      />
                                    )}
                                    {packUnitLabel && (
                                      <CustomCsPcInput
                                        value={invoice.pieces}
                                        onChange={() => null}
                                        label={packUnitLabel}
                                        percentage={75}
                                        borderColor={borderColor}
                                        backgroundColor={backgroundColor}
                                        readOnly={true}
                                      />
                                    )}
                                  </div>
                                </div>
                              </div>

                              {invoice.approvals?.length > 0 && (
                                <div className="open-claims-approval-section">
                                  {invoice.approvals.some((approval: any) => approval.status !== 'PENDING') && (
                                    <div className="open-claims-batch-id" style={{ fontWeight: 500, marginBottom: "4px" }}>
                                      Approved / Rejected Amt by
                                    </div>
                                  )}
                                  {invoice.approvals
                                    .filter((approval: any) => approval.status !== 'PENDING')
                                    .reduce((acc: any[], approval: any) => {
                                      const existing = acc.find(
                                        (item) =>
                                          item.designation === approval.designation &&
                                          item.userId === approval.userId
                                      );
                                      if (existing) {
                                        existing.approvedAmount = formatDecimal(parseFloat(existing.approvedAmount) + parseFloat(approval.approvedAmount));
                                      } else {
                                        acc.push({ ...approval });
                                      }
                                      return acc;
                                    }, []).sort((a: { designation: string }, b: { designation: string }) => {
                                      const aLevel = approvalConfig?.flow.findIndex((level: any) =>
                                        level.includes(a.designation))
                                      const bLevel = approvalConfig?.flow.findIndex((level: any) =>
                                        level.includes(b.designation))
                                      return bLevel - aLevel;
                                    })
                                    .map((approval: any, aIndex: number) => {
                                      const isApproved = approval.status === 'APPROVED';
                                      const isRejected = approval.status === 'REJECTED';
                                      const borderColor = isApproved ? "#00C6B1" : isRejected ? "#C44C36" : "#dfe2e7";

                                      return (
                                        <div key={`approval-${aIndex}`} className="open-claims-approval-item">
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'space-between' }}>
                                            <div className="open-claims-approval-designation claims-text-ellipsis" title={approval.designation || 'Unknown'}>
                                              {approval.designation || 'Unknown'}
                                            </div>
                                            <div className="open-claims-invoice-discount-box"
                                              style={{ borderColor, backgroundColor: isApproved ? "#FAFFFF" : isRejected ? "#FFFAFA" : "#FFFFFF" }}>
                                              <div className="open-claims-invoice-discount-label">Discount Amount</div>
                                              <div className="open-claims-invoice-discount-value">
                                                {currency} {formatDecimal(invoice.discount)}
                                              </div>
                                            </div>
                                          </div>
                                          {approval.status === 'REJECTED' && (
                                            <div className="open-claims-rejection-reason">
                                              <div className='open-claims-rejection-label'>
                                                Rejection Reason </div>
                                              {approval.reason || 'No reason provided'}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )
                    }))}

              </div>
            </div>}
        </div>

      <div className="open-claims-modal-footer">
        <div className="open-claims-footer-content">
          {secondLastSegment === "open" ? (
            <>
              <div className="open-claims-claim-amount-container">
                <span className="open-claims-claim-amount-label">Claim Amount</span>
                <span className="open-claims-claim-amount-value">
                  {selectedOpenClaimsRow.claimAmount}
                </span>
              </div>
              <div className="open-claims-claim-amount-container">
                <span className="open-claims-claim-amount-label">Approved Claim Amount</span>
                <span className="open-claims-claim-amount-value">
                  {selectedOpenClaimsRow.approvedClaimAmount}
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="open-claims-claim-amount-container">
                <span className="open-claims-claim-amount-label">Approved Claim Amount</span>
                <span className="open-claims-claim-amount-value">
                  {selectedOpenClaimsRow.approvedClaimAmount}
                </span>
              </div>
              <div className="open-claims-claim-amount-container">
                {statusConfig && (
                  <OrderStatusCell orderStatusConfig={statusConfig} statusId={getSelectedStatus()} />
                )}
              </div>
            </>
          )}
          {secondLastSegment === "open" ?
            <div className="open-claims-action-buttons">
              <button
                className="open-claims-reject-outline-button"
                onClick={() => {
                  setRejectionModalOpen(true);
                }}
                disabled={!isAnyBatchSelected || !canApproveReject}
                title={!canApproveReject ? "Claim is locked for editing" : ""}
                >
                Reject
              </button>
              <button
                className="open-claims-approve-outline-button"
                onClick={handleApproveClick}
                disabled={!isAnyBatchSelected || !canApproveReject}
                title={!canApproveReject ? "Claim is locked for editing" : ""}
                >
                Approve
              </button>
            </div> : <></>}
        </div>
      </div>

      <ApprovalModal
        approvalModalOpen={approvalModalOpen}
        setApprovalModalOpen={setApprovalModalOpen}
        approvalModalState={approvalModalState}
        setApprovalModalState={setApprovalModalState}
        postBody={{}}
        isOpenClaimsPage={true}
        openClaimsRows={openClaimsRows}
        selectedOpenClaimsRow={selectedOpenClaimsRow}
        updateSelectedRowInRows={updateSelectedRowInRows}
        claimType={accordionData.claimType}
        setIsModalOpen={setIsModalOpen}
      />
      <ClaimRejectionModal
        isOpen={rejectionModalOpen}
        onClose={() => setRejectionModalOpen(false)}
        heading={
          accordionData.claimType === claimTypes[0] 
            ? "Enter the reasons for rejected qty in Batch IDs" 
            : accordionData.claimType === claimTypes[1]
            ? "Enter the reasons for rejected claim amount of invoices"
            : `Request is being rejected for ${selectedOpenClaimsRow?.outletName} (${selectedOpenClaimsRow?.outletCode})`
        }
        selectedBatches={getSelectedBatchesArray()}
        onConfirm={handleRejectionClick}
      />
    </div>
    </>
  );
};

export default OpenClaimsDetailsModal;